/*
  KOC reviews widget behaviour (snippets/koc-reviews.liquid).

  - One list. Default ("Featured") order: the family's `initial` reviews from
    the [data-koc-initial] JSON (picks first in dashboard order, then newest;
    the same reviews Liquid rendered), then every other feed review newest
    first. A review is keyed by src + id (ids are per store); a pick or an
    initial review never shows twice.
  - The first data-slots reviews show (data-slots-mobile under 750px), then
    "Load more" adds data-step. Any sort or filter drops the pins and orders
    everything by the shopper's choice; "Reset" returns to the pinned default.
  - Full review list: fetched once per family from feed_url (static JSON on
    Shopify Files, never Railway) when the list nears the viewport or "Load
    more" is clicked. Feed rows: {id, src store, n name, r rating, t title,
    b body, d YYYY-MM-DD, v verified (1|null), l "7.5' Slim · LED" label,
    img [urls]}.
  - Filters (stars, with photos, size, lights; on phones size and lights are
    one dropdown, one choice at a time), sort, keyword search, photo
    lightbox.
  - "Write a review" loads Judge.me on the first tap (Oct 29 2026): the hidden
    widget is built from the host's data attributes into the host and Judge.me's
    loader (app embed, loader.js) runs once more, because the loader scans the
    page only once and loads its widget bundle only when it finds a widget.
    Then the tap clicks Judge.me's own "Write a review" button, so the form is
    Judge.me's. Review-request links with judgeme_* parameters load it at once.
    Judge.me binds once per page load; after a combined-listing swap to
    another product a loaded widget still belongs to the first one, so the
    click reloads the current product with #koc-write-review and the form
    opens there.
  - Judge.me adds its own Product + AggregateRating JSON-LD
    (script.jdgm-aggregate-rating-jld) when it finds no rating on the page.
    Once Judge.me is loaded here, that script is removed: the page's one
    rating is the theme's (sections/product-information.liquid), or none for
    a family with 0 reviews.
  - Empty state ([data-koc-reviews-empty], 0 reviews): only Write a review.
  - Deep links #judgeme_product_reviews / #jdgm... scroll here. The root's id
    is "koc-reviews" (an element with id judgeme_product_reviews makes
    Judge.me's loader fetch its form bundle at page load), so these links are
    handled here, never by the browser's own anchor jump.
  - While a widget is on screen, <html> carries data-koc-reviews-in-view (theme
    CSS uses it to move the sticky add-to-cart bar out of the way).

  Morph-safe: Horizon morphs the whole <main> when a combined-listing option
  loads another product, which resets this widget's markup to the new
  product's server HTML. Every listener is delegated from the root element
  (bound once, per root), elements are looked up when used, and sync() runs
  after each swap: same family keeps the shopper's sort, filters, search and
  loaded reviews; another family starts fresh.

  Self-contained: no theme scripts, no globals beyond a one-time init flag.
*/
(() => {
  if (window.__kocReviewsInit) return;
  window.__kocReviewsInit = true;

  // Size filter groups, in feet, matching the site's collection menu. A tree
  // height (Slim / Full / Pencil ignored) joins the group whose range holds
  // it; a height outside every range gets its own option ("15'"). Wreath and
  // garland sizes (24", 36", 9' × 12") are not heights and keep their labels.
  const SIZE_GROUPS = [
    { label: "3' tabletop", min: 3, max: 3.99 }, // the tabletop trees in the wreath / garland families
    { label: "4–5'", min: 4, max: 5.99 },
    { label: "6 & 6.5'", min: 6, max: 6.99 },
    { label: "7 & 7.5'", min: 7, max: 7.99 },
    { label: "8'", min: 8, max: 8.99 },
    { label: "9'", min: 9, max: 9.99 },
    { label: "10'", min: 10, max: 11.99 },
    { label: "12'", min: 12, max: 14.99 },
  ];
  const TREE_HEIGHT = /^(\d+(?:\.\d+)?)'(?:\s+(?:slim|full|pencil))?$/i;

  // Judge.me's loader opens its own form flows for these (review-request emails).
  const JDGM_PARAMS = /[?&](judgeme_token|judgeme_review_uuid|judgeme_dynamic_form|judgeme_follow_up_token|judgeme_upload_pictures|judgeme_pre_verified)=/;
  const CLAMP_AT = 420;
  const DEEP_LINK = /^#(judgeme_product_reviews|jdgm)/i;
  const WRITE_HASH = '#koc-write-review';
  const LIGHT_RE = /\b(led|unlit)\b/i;
  const MOBILE = '(max-width: 749px)';
  // Phones: the merged Size & lights dropdown and its status line (koc-reviews.css).
  const PHONE = '(max-width: 599px)';
  const instances = new WeakMap();

  // Widget roots currently on screen; drives <html data-koc-reviews-in-view>.
  const inView = new Set();
  const syncInView = () => {
    inView.forEach((root) => { if (!root.isConnected) inView.delete(root); });
    document.documentElement.toggleAttribute('data-koc-reviews-in-view', inView.size > 0);
  };
  const viewObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? inView.add(entry.target) : inView.delete(entry.target)));
          syncInView();
        })
      : null;

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };

  const formatDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return iso || '';
    const date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  const formatNumber = (n) => n.toLocaleString('en-US');

  // Judge.me serves sizes by suffix: __compact (160px), __huge (1024px), __original.
  const imageVariant = (url, size) => String(url).replace(/__original\.(\w+)/i, `__${size}.$1`);

  const splitLabel = (label) => {
    let size = '';
    let light = '';
    String(label || '')
      .split(' · ')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        if (LIGHT_RE.test(part)) light = part;
        else if (!size) size = part;
      });
    return { size, light };
  };

  // The size option a label part belongs to: { key, rank } (rank in inches,
  // for sorting), or null for no size ("Slim" alone, blank).
  const sizeGroup = (part) => {
    const text = String(part || '').trim();
    const tree = TREE_HEIGHT.exec(text);
    if (tree) {
      const feet = parseFloat(tree[1]);
      const group = SIZE_GROUPS.find((g) => feet >= g.min && feet <= g.max);
      return group ? { key: group.label, rank: group.min * 12 } : { key: `${feet}'`, rank: feet * 12 };
    }
    // Wreath / garland: keep the label; rank by its first measure in inches.
    const m = /(\d+(?:\.\d+)?)\s*(['"])/.exec(text);
    if (!m) return null;
    return { key: text, rank: parseFloat(m[1]) * (m[2] === "'" ? 12 : 1) };
  };

  // src + id; a content key only for a row that lacks either.
  const reviewKey = (r) =>
    r.src && r.id != null && r.id !== ''
      ? `${r.src}:${r.id}`
      : [r.n || '', r.d || '', Math.round(Number(r.r)) || 0, Array.from(String(r.b || '').replace(/[\r\n]+/g, '')).slice(0, 24).join('')].join('|');

  const normalize = (r, i) => {
    const { size, light } = splitLabel(r.l);
    const group = sizeGroup(size);
    return {
      ...r,
      r: Math.round(Number(r.r)),
      i,
      size: group ? group.key : '',
      sizeRank: group ? group.rank : Infinity,
      light,
      imgs: Array.isArray(r.img) ? r.img.filter(Boolean) : [],
      key: reviewKey(r),
      ts: dateValue(r.d),
      text: null,
    };
  };

  const isVerified = (r) => r.v === 1 || r.v === true;

  // Review date as a real timestamp (UTC midnight); undated rows sort last.
  const dateValue = (d) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : -Infinity;
  };

  // Newest first; same day: higher review id first (ids grow over time), then
  // src, so the order never depends on where a row came from.
  const byNewest = (a, b) =>
    b.ts - a.ts || (Number(b.id) || 0) - (Number(a.id) || 0) || String(a.src || '').localeCompare(String(b.src || ''));

  const readJson = (root, sel) => {
    try {
      const data = JSON.parse(root.querySelector(sel)?.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  };

  class KocReviews {
    constructor(root) {
      this.root = root;
      viewObserver?.observe(root);
      this.bind();
      this.reset();
    }

    $(sel) {
      return this.root.querySelector(sel);
    }

    get list() { return this.$('[data-koc-list]'); }
    get status() { return this.$('[data-koc-status]'); }
    get controls() { return this.$('[data-koc-controls]'); }
    get moreBtn() { return this.$('[data-koc-more]'); }
    get search() { return this.$('[data-koc-q]'); }
    get sort() { return this.$('[data-koc-sort]'); }
    get sizeSel() { return this.$('[data-koc-size]'); }
    get lightSel() { return this.$('[data-koc-light]'); }
    get variantSel() { return this.$('[data-koc-variant]'); }
    get lightbox() { return this.$('[data-koc-lightbox]'); }

    familyKey() {
      const ds = this.root.dataset;
      return ds.familyKey || String(ds.feed || '').split('?')[0];
    }

    // (Re)read the family from the markup and start from its default view.
    reset() {
      const ds = this.root.dataset;
      this.family = this.familyKey();
      this.feedUrl = ds.feed;
      this.total = parseInt(ds.count, 10) || 0;
      this.slots = parseInt(ds.slots, 10) || 4;
      this.slotsMobile = parseInt(ds.slotsMobile, 10) || 2;
      this.step = parseInt(ds.step, 10) || 10;
      this.sizeMin = parseInt(ds.sizeMin, 10) || 5;
      this.pickKeys = new Set(readJson(this.root, '[data-koc-picks]').map(String));
      this.initial = readJson(this.root, '[data-koc-initial]')
        .filter((r) => r && Number(r.r) >= 1)
        .map((r, i) => normalize(r, -1000 + i));
      this.initialKeys = new Set(this.initial.map((r) => r.key));
      this.defaultSort = this.pickKeys.size ? 'featured' : 'newest';
      this.reviews = null;
      this.loading = null;
      this.loadFor = null;
      this.state = this.freshState();
      this.view = [];
      this.shown = 0;
      this.target = 0;
      this.render();
      this.observe();
    }

    // After a morph: same family keeps state and loaded reviews, else reset.
    // data-koc-ready is set by render(); a morph resets the root's attributes
    // to the server markup, so its absence means the markup was replaced.
    sync() {
      if (this.familyKey() !== this.family) {
        this.reset();
        return;
      }
      if (this.root.hasAttribute('data-koc-ready')) return;
      const ds = this.root.dataset;
      this.slots = parseInt(ds.slots, 10) || this.slots;
      this.slotsMobile = parseInt(ds.slotsMobile, 10) || this.slotsMobile;
      this.step = parseInt(ds.step, 10) || this.step;
      this.sizeMin = parseInt(ds.sizeMin, 10) || this.sizeMin;
      const shown = this.shown;
      this.render();
      // Put back as many cards as the shopper had open.
      if (shown > this.shown) {
        this.target = shown;
        this.fill(false);
      }
      if (!this.reviews) this.observe();
    }

    // Server markup -> current state: options, control values, list.
    render() {
      if (this.reviews) {
        this.buildOptions();
        if (this.controls) this.controls.hidden = false;
      }
      const s = this.state;
      if (this.search) this.search.value = s.qRaw || '';
      if (this.sort) this.sort.value = s.sort;
      if (this.sizeSel) this.sizeSel.value = s.size;
      if (this.lightSel) this.lightSel.value = s.light;
      this.apply();
      this.root.setAttribute('data-koc-ready', '');
    }

    freshState() {
      return { stars: new Set(), photos: false, size: '', light: '', sort: this.defaultSort, q: '', qRaw: '', autoUnpinned: false };
    }

    firstCount() {
      return window.matchMedia(MOBILE).matches ? this.slotsMobile : this.slots;
    }

    // A filter or search means the shopper's order, not ours: leave "Featured".
    // Remembered, so clearing the last filter brings the pins back (repin).
    unpin() {
      if (this.state.sort !== 'featured') return;
      this.state.sort = 'newest';
      this.state.autoUnpinned = true;
      if (this.sort) this.sort.value = 'newest';
    }

    repin() {
      if (!this.state.autoUnpinned || this.hasFilters()) return;
      this.state.sort = this.defaultSort;
      this.state.autoUnpinned = false;
      if (this.sort) this.sort.value = this.defaultSort;
    }

    bind() {
      const root = this.root;

      root.addEventListener('click', (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        const hit = (sel) => t.closest(sel);
        let node;

        if ((node = hit('[data-koc-photo]'))) {
          const group = [...node.parentElement.querySelectorAll('[data-koc-photo]')];
          this.openLightbox(group, group.indexOf(node), node);
        } else if (hit('[data-koc-lb-close]')) {
          this.lightbox?.close();
        } else if (hit('[data-koc-lb-prev]')) {
          this.stepLightbox(-1);
        } else if (hit('[data-koc-lb-next]')) {
          this.stepLightbox(1);
        } else if (t === this.lightbox || t.classList.contains('koc-reviews__lightbox-inner')) {
          this.lightbox?.close();
        } else if (hit('[data-koc-more]')) {
          this.loadMore();
        } else if (hit('[data-koc-clear-stars]')) {
          this.state.stars = new Set();
          this.apply();
        } else if (hit('[data-koc-clear-choice]')) {
          this.state.stars = new Set();
          this.state.size = '';
          this.state.light = '';
          if (this.sizeSel) this.sizeSel.value = '';
          if (this.lightSel) this.lightSel.value = '';
          this.apply();
        } else if (hit('[data-koc-clear]')) {
          this.clearFilters();
        } else if (hit('[data-koc-retry]')) {
          this.loadMore();
        } else if ((node = hit('[data-koc-write]'))) {
          this.writeReview(node);
        } else if ((node = hit('[data-koc-star-filter]'))) {
          this.barFilter(Number(node.dataset.kocStarFilter));
        } else if ((node = hit('[data-koc-chip-star]'))) {
          const star = Number(node.dataset.kocChipStar);
          if (this.state.stars.has(star)) this.state.stars.delete(star);
          else this.state.stars.add(star);
          this.unpin();
          this.apply();
        } else if (hit('[data-koc-chip-photos]')) {
          this.state.photos = !this.state.photos;
          this.unpin();
          this.apply();
        } else if ((node = hit('[data-koc-toggle-body]'))) {
          const body = node.parentElement?.querySelector('.koc-review__body');
          if (!body) return;
          const open = body.classList.toggle('koc-review__body--clamped') === false;
          node.textContent = open ? 'Show less' : 'Read more';
          node.setAttribute('aria-expanded', String(open));
        }
      });

      root.addEventListener('change', (e) => {
        const t = e.target;
        if (!(t instanceof HTMLSelectElement)) return;
        if (t.matches('[data-koc-sort]')) {
          this.state.sort = t.value;
          this.state.autoUnpinned = false;
        } else if (t.matches('[data-koc-size]')) {
          this.state.size = t.value;
          this.unpin();
        } else if (t.matches('[data-koc-light]')) {
          this.state.light = t.value;
          this.unpin();
        } else if (t.matches('[data-koc-variant]')) {
          // Phones: one choice at a time; a size clears the light and vice versa.
          const [kind, ...rest] = t.value.split(':');
          const value = rest.join(':');
          this.state.size = kind === 'size' ? value : '';
          this.state.light = kind === 'light' ? value : '';
          if (this.sizeSel) this.sizeSel.value = this.state.size;
          if (this.lightSel) this.lightSel.value = this.state.light;
          if (t.value) this.unpin();
        } else if (t.matches('[data-koc-rating]')) {
          // Phone rating dropdown: the same star filter as the bars.
          this.state.stars = t.value ? new Set([Number(t.value)]) : new Set();
          if (t.value) this.unpin();
        } else {
          return;
        }
        this.apply();
      });

      let timer;
      root.addEventListener('input', (e) => {
        const t = e.target;
        if (!(t instanceof HTMLInputElement) || !t.matches('[data-koc-q]')) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          this.state.qRaw = t.value;
          this.state.q = t.value.trim().toLowerCase();
          if (this.state.q) this.unpin();
          this.apply();
        }, 200);
      });

      root.addEventListener('keydown', (e) => {
        if (!this.lightbox?.open) return;
        if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        if (e.key === 'ArrowRight') this.stepLightbox(1);
      });

      let x0 = null;
      root.addEventListener('touchstart', (e) => {
        x0 = this.lightbox?.open && this.lightbox.contains(e.target) ? e.touches[0].clientX : null;
      }, { passive: true });
      root.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) this.stepLightbox(dx < 0 ? 1 : -1);
      });

      // close and error do not bubble: capture them at the root.
      root.addEventListener('close', (e) => {
        if (e.target !== this.lightbox) return;
        document.documentElement.style.removeProperty('overflow');
        if (this.lbTrigger?.isConnected) this.lbTrigger.focus();
      }, true);
      root.addEventListener('error', (e) => {
        const img = e.target;
        if (!(img instanceof HTMLImageElement)) return;
        if (img.matches('[data-koc-lb-img]')) {
          const orig = this.lbPhotos?.[this.lbIndex]?.dataset.orig;
          if (orig && img.src !== orig) img.src = orig;
        } else if (img.dataset.orig && img.src !== img.dataset.orig) {
          img.src = img.dataset.orig;
        }
      }, true);
    }

    observe() {
      this.io?.disconnect();
      const target = this.$('[data-koc-all]');
      if (!target || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.io.disconnect();
            this.load();
          }
        },
        { rootMargin: '300px 0px' }
      );
      this.io.observe(target);
    }

    load() {
      if (this.reviews) return Promise.resolve();
      if (this.loading && this.loadFor === this.family) return this.loading;

      const family = this.family;
      this.loadFor = family;
      this.loading = fetch(this.feedUrl, { credentials: 'omit' })
        .then((res) => {
          if (!res.ok) throw new Error(`feed ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (family !== this.family) return; // swapped to another family meanwhile
          const rows = Array.isArray(data) ? data : Array.isArray(data?.reviews) ? data.reviews : null;
          if (!rows) throw new Error('feed shape');
          this.reviews = rows.filter((r) => r && Number(r.r) >= 1).map(normalize);
          this.buildOptions();
          if (this.controls) this.controls.hidden = false;
          // The cards on screen are the start of the default view, so only
          // append up to the current target.
          this.apply({ keep: true });
        })
        .catch(() => {
          if (family !== this.family) return;
          this.loading = null;
          this.setStatus('');
          const msg = el('span', null, 'We couldn’t load all reviews right now. ');
          const retry = el('button', null, 'Try again');
          retry.type = 'button';
          retry.dataset.kocRetry = '';
          this.status?.append(msg, retry);
          if (this.moreBtn) this.moreBtn.hidden = true;
        })
        .finally(() => this.moreBtn?.removeAttribute('aria-busy'));

      return this.loading;
    }

    // Size: groups with at least data-size-min reviews, by height (reviews in
    // smaller groups still show under "All sizes"). Lights: every value.
    buildOptions() {
      // Options show just the value, no review count.
      const fill = (select, wrap, values, order, min = 1) => {
        if (!select) return;
        while (select.options.length > 1) select.remove(1);
        const sorted = [...values.entries()].filter(([, n]) => n >= min).sort(order);
        sorted.forEach(([value]) => {
          const opt = el('option', null, value);
          opt.value = value;
          select.append(opt);
        });
        const wrapEl = this.$(wrap);
        if (wrapEl) wrapEl.hidden = sorted.length < 2;
      };
      const sizes = new Map();
      const ranks = new Map();
      const lights = new Map();
      this.reviews.forEach((r) => {
        if (r.size) {
          sizes.set(r.size, (sizes.get(r.size) || 0) + 1);
          ranks.set(r.size, r.sizeRank);
        }
        if (r.light) lights.set(r.light, (lights.get(r.light) || 0) + 1);
      });
      fill(this.sizeSel, '[data-koc-size-wrap]', sizes, (a, b) => ranks.get(a[0]) - ranks.get(b[0]) || a[0].localeCompare(b[0]), this.sizeMin);
      fill(this.lightSel, '[data-koc-light-wrap]', lights, (a, b) => a[0].localeCompare(b[0]));
      this.fillVariant();

      // "With photos (497)"; no toggle for a family without photo reviews.
      const photos = this.reviews.filter((r) => r.imgs.length).length;
      const toggle = this.$('[data-koc-chip-photos]');
      if (toggle) toggle.hidden = photos === 0 && !this.state.photos;
      const count = this.$('[data-koc-photos-count]');
      if (count) count.textContent = ` (${formatNumber(photos)})`;
      // A size picked before (e.g. kept across a morph) that is no longer an option.
      if (this.sizeSel && this.state.size && ![...this.sizeSel.options].some((o) => o.value === this.state.size)) {
        this.state.size = '';
      }
    }

    // Phones: the two selects' options as one dropdown, in a "Sizes" and a
    // "Lights" group (each only when its own select shows). Default label
    // "Size & lights", "All sizes" or "All lights"; hidden with neither.
    fillVariant() {
      const select = this.variantSel;
      if (!select) return;
      const groups = [
        ['Sizes', 'size', this.sizeSel, '[data-koc-size-wrap]'],
        ['Lights', 'light', this.lightSel, '[data-koc-light-wrap]'],
      ].filter(([, , sel, wrap]) => sel && this.$(wrap) && !this.$(wrap).hidden);
      const kinds = groups.map(([, kind]) => kind);
      const label = kinds.length === 2 ? 'Size & lights' : kinds[0] === 'size' ? 'All sizes' : 'All lights';
      const first = el('option', null, label);
      first.value = '';
      select.replaceChildren(first);
      groups.forEach(([title, kind, sel]) => {
        const og = el('optgroup');
        og.label = title;
        [...sel.options].slice(1).forEach((o) => {
          const opt = el('option', null, o.textContent);
          opt.value = `${kind}:${o.value}`;
          og.append(opt);
        });
        select.append(og);
      });
      const lab = this.$('[data-koc-variant-label]');
      if (lab) lab.textContent = label;
      const wrap = this.$('[data-koc-variant-wrap]');
      if (wrap) wrap.hidden = groups.length === 0;
    }

    hasFilters() {
      const s = this.state;
      return Boolean(s.stars.size > 0 || s.photos || s.size || s.light || s.q);
    }

    // Anything other than the default view (filters, search, or another sort).
    isFiltered() {
      return this.hasFilters() || this.state.sort !== this.defaultSort;
    }

    async barFilter(star) {
      const only = this.state.stars.size === 1 && this.state.stars.has(star);
      this.state.stars = only ? new Set() : new Set([star]);
      this.unpin();
      this.apply();
      await this.load();
      this.apply();
      // Bring the list into view only when its top is off-screen (or under a
      // sticky header).
      const list = this.list;
      if (!list) return;
      const top = list.getBoundingClientRect().top;
      if (top < 80 || top > window.innerHeight - 120) {
        (this.status || list).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // keep: the feed just arrived under unchanged settings; append instead of
    // rebuilding, so the cards already on screen stay put.
    apply({ keep = false } = {}) {
      const list = this.list;
      if (!list) return;
      this.repin();
      const s = this.state;
      const terms = s.q ? s.q.split(/\s+/).filter(Boolean) : [];
      const byDate = byNewest;

      // Pins exist only in the default view. Any sort or filter works on the
      // feed rows alone (the initial reviews are in the feed too), so a pick
      // or an initial review is sorted and filtered like every other row.
      let rows;
      if (!this.isFiltered()) {
        // Default view: the initial reviews (picks, then newest), then every
        // other review newest first.
        const rest = (this.reviews || [])
          .filter((r) => !this.initialKeys.has(r.key) && !this.pickKeys.has(r.key))
          .sort(byDate);
        rows = this.initial.concat(rest);
      } else {
        rows = this.filterRows(this.reviews || [], terms);
        const sorters = {
          newest: byDate,
          featured: byDate,
          highest: (a, b) => b.r - a.r || byDate(a, b),
          lowest: (a, b) => a.r - b.r || byDate(a, b),
          photos: (a, b) => (b.imgs.length > 0) - (a.imgs.length > 0) || byDate(a, b),
        };
        rows = rows.slice().sort(sorters[s.sort] || byDate);
      }

      this.view = rows;
      this.syncControls();
      if (!keep) {
        this.shown = 0;
        this.target = this.firstCount();
        list.replaceChildren();
        if (!rows.length && this.reviews) {
          const empty = el('p', 'koc-reviews__empty', 'No reviews match these filters. ');
          const clear = el('button', 'koc-review__more', 'Reset');
          clear.type = 'button';
          clear.dataset.kocClear = '';
          empty.append(clear);
          list.append(empty);
        }
      }
      this.fill(false);
    }

    filterRows(source, terms) {
      const s = this.state;
      return source.filter((r) => {
        if (s.stars.size && !s.stars.has(r.r)) return false;
        if (s.photos && !r.imgs.length) return false;
        if (s.size && r.size !== s.size) return false;
        if (s.light && r.light !== s.light) return false;
        if (terms.length) {
          if (r.text === null) r.text = `${r.t || ''} ${r.b || ''} ${r.n || ''} ${r.l || ''}`.toLowerCase();
          if (!terms.every((term) => r.text.includes(term))) return false;
        }
        return true;
      });
    }

    clearFilters() {
      this.state = this.freshState();
      if (this.search) this.search.value = '';
      if (this.sort) this.sort.value = this.defaultSort;
      if (this.sizeSel) this.sizeSel.value = '';
      if (this.lightSel) this.lightSel.value = '';
      this.apply();
      (this.search || this.sort)?.focus();
    }

    syncControls() {
      const s = this.state;
      this.root.querySelectorAll('[data-koc-chip-star]').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(s.stars.has(Number(chip.dataset.kocChipStar))));
      });
      this.root.querySelectorAll('[data-koc-star-filter]').forEach((bar) => {
        const star = Number(bar.dataset.kocStarFilter);
        bar.setAttribute('aria-pressed', String(s.stars.size === 1 && s.stars.has(star)));
      });
      this.$('[data-koc-chip-photos]')?.setAttribute('aria-checked', String(s.photos));
      const rating = this.$('[data-koc-rating]');
      if (rating) rating.value = s.stars.size === 1 ? String([...s.stars][0]) : '';
      const variant = this.variantSel;
      if (variant) {
        variant.value = s.size ? `size:${s.size}` : s.light ? `light:${s.light}` : '';
      }
    }

    async loadMore() {
      this.target = Math.max(this.target, this.shown) + this.step;
      if (!this.reviews) {
        this.moreBtn?.setAttribute('aria-busy', 'true');
        const before = this.shown;
        await this.load();
        if (this.shown > before) this.focusFirst(before);
        return;
      }
      this.fill(true);
    }

    // Render view rows up to this.target.
    fill(focusNew) {
      const list = this.list;
      if (!list) return;
      const start = this.shown;
      const next = this.view.slice(start, Math.max(start, this.target));
      const frag = document.createDocumentFragment();
      next.forEach((r) => frag.append(this.card(r)));
      list.append(frag);
      this.shown = start + next.length;

      // Before the feed arrives the view is the initial reviews only; the
      // family count is the real total.
      const total = this.reviews ? this.view.length : Math.max(this.total, this.view.length);
      if (this.moreBtn) this.moreBtn.hidden = this.shown >= total;
      this.updateStatus(total);
      if (focusNew && next.length) this.focusFirst(start);
    }

    // One line above the list:
    //   star filter   "Showing 200 4★ reviews · Clear"   (Clear drops the stars)
    //   other filter  "Showing 32 matching reviews · Reset"
    //   no filter     "Showing 4 of 3,439 reviews"        (a sort alone is no filter)
    updateStatus(total) {
      if (!this.status) return;
      const s = this.state;
      const noun = (n, word) => `${word}${n === 1 ? '' : 's'}`;
      if (!this.reviews) {
        // Filters only apply once the feed is in; until then count the initial cards.
        this.setStatus(this.shown ? `Showing ${formatNumber(this.shown)} of ${formatNumber(total)} ${noun(total, 'review')}` : '');
        return;
      }
      const choice = s.size || s.light;
      if (choice && !(s.size && s.light) && !s.photos && !s.q && window.matchMedia(PHONE).matches) {
        // Phones: "Showing 41 4★ reviews · 24" · Clear"; Clear drops both.
        const stars = s.stars.size ? `${[...s.stars].sort((a, b) => b - a).map((n) => `${n}★`).join(', ')} ` : '';
        this.setStatus(`Showing ${formatNumber(total)} ${stars}${noun(total, 'review')}`, 'Clear', 'kocClearChoice', choice);
      } else if (s.stars.size) {
        const stars = [...s.stars].sort((a, b) => b - a).map((n) => `${n}★`).join(', ');
        this.setStatus(`Showing ${formatNumber(total)} ${stars} ${noun(total, 'review')}`, 'Clear', 'kocClearStars');
      } else if (this.hasFilters()) {
        this.setStatus(`Showing ${formatNumber(total)} matching ${noun(total, 'review')}`, 'Reset', 'kocClear');
      } else {
        this.setStatus(`Showing ${formatNumber(this.shown)} of ${formatNumber(total)} ${noun(total, 'review')}`);
      }
    }

    focusFirst(index) {
      const card = this.list?.children[index];
      if (!card) return;
      card.setAttribute('tabindex', '-1');
      card.focus({ preventScroll: false });
    }

    // text, plus an optional " · Clear"-style link button (data attribute name).
    // An optional note (the phone size-or-lights choice) goes between the two.
    setStatus(text, action, dataKey, note) {
      const status = this.status;
      if (!status) return;
      status.textContent = text;
      if (!action) return;
      const mkSep = () => {
        const s = el('span', 'koc-reviews__status-sep', '·');
        s.setAttribute('aria-hidden', 'true');
        return s;
      };
      if (note) status.append(' ', mkSep(), ' ', note);
      const sep = mkSep();
      const btn = el('button', null, action);
      btn.type = 'button';
      btn.dataset[dataKey] = '';
      status.append(' ', sep, ' ', btn);
    }

    card(r) {
      const card = el('article', 'koc-review koc-review--list');
      card.dataset.kocKey = r.key;
      const main = el('div', 'koc-review__main');

      const head = el('div', 'koc-review__head');
      const starsWrap = el('span', 'koc-stars koc-stars--review');
      const stars = el('span', 'koc-stars__icons');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `Rated ${r.r} out of 5 stars`);
      stars.style.setProperty('--koc-stars-pct', `${r.r * 20}%`);
      starsWrap.append(stars);
      head.append(starsWrap);
      if (r.d) {
        const time = el('time', 'koc-review__date', formatDate(r.d));
        time.dateTime = r.d;
        head.append(time);
      }
      main.append(head);

      if (r.t) main.append(el('h4', 'koc-review__title', r.t));

      const body = el('p', 'koc-review__body', r.b || '');
      main.append(body);
      if ((r.b || '').length > CLAMP_AT) {
        body.classList.add('koc-review__body--clamped');
        const more = el('button', 'koc-review__more', 'Read more');
        more.type = 'button';
        more.dataset.kocToggleBody = '';
        more.setAttribute('aria-expanded', 'false');
        main.append(more);
      }

      if (r.imgs.length) {
        const photos = el('div', 'koc-review__photos');
        r.imgs.slice(0, 6).forEach((url, idx, arr) => {
          const btn = el('button', 'koc-review__photo');
          btn.type = 'button';
          btn.dataset.kocPhoto = '';
          btn.dataset.full = imageVariant(url, 'huge');
          btn.dataset.orig = url;
          btn.setAttribute('aria-label', `View photo ${idx + 1} of ${arr.length} from ${r.n || 'a customer'}`);
          const img = el('img');
          img.alt = '';
          img.width = 80;
          img.height = 80;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.dataset.orig = url;
          img.src = imageVariant(url, 'compact');
          btn.append(img);
          photos.append(btn);
        });
        main.append(photos);
      }

      const foot = el('div', 'koc-review__foot');
      foot.append(el('span', 'koc-review__name', r.n || 'Customer'));
      if (isVerified(r)) foot.append(el('span', 'koc-review__verified', 'Verified Buyer'));
      if (r.l) foot.append(el('span', 'koc-review__label', r.l));
      card.append(main, foot);

      return card;
    }

    /* Lightbox */
    openLightbox(photos, index, trigger) {
      const lb = this.lightbox;
      if (!lb || typeof lb.showModal !== 'function') {
        window.open(photos[index].dataset.orig, '_blank', 'noopener');
        return;
      }
      this.lbPhotos = photos;
      this.lbTrigger = trigger;
      this.showPhoto(index);
      lb.showModal();
      document.documentElement.style.overflow = 'hidden';
      lb.querySelector('[data-koc-lb-close]')?.focus();
    }

    stepLightbox(delta) {
      if (!this.lightbox?.open || !this.lbPhotos || this.lbPhotos.length < 2) return;
      const n = this.lbPhotos.length;
      this.showPhoto((this.lbIndex + delta + n) % n);
    }

    showPhoto(index) {
      const lb = this.lightbox;
      this.lbIndex = index;
      const n = this.lbPhotos.length;
      const img = lb.querySelector('[data-koc-lb-img]');
      const count = lb.querySelector('[data-koc-lb-count]');
      img.src = this.lbPhotos[index].dataset.full;
      img.alt = this.lbPhotos[index].getAttribute('aria-label')?.replace(/^View /, '') || 'Review photo';
      count.textContent = n > 1 ? `${index + 1} / ${n}` : '';
      lb.querySelector('[data-koc-lb-prev]').hidden = n < 2;
      lb.querySelector('[data-koc-lb-next]').hidden = n < 2;
    }

    writeReview(button) {
      writeReview(button, this.root.dataset.productId);
    }

    /* Deep links: reveal the tab panel we sit in (if any), then scroll. */
    reveal() {
      const panel = this.root.closest('[role="tabpanel"]');
      if (panel && !panel.offsetParent && panel.dataset.panel) {
        const scope = panel.closest('section') || document;
        scope.querySelector(`[role="tab"][data-tab="${panel.dataset.panel}"]`)?.click();
      }
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.load();
    }
  }

  // Judge.me's JSON-LD rating is not ours to show (see top); remove it whenever
  // Judge.me adds it.
  let schemaWatch = null;
  const dropJudgemeSchema = () => {
    document.querySelectorAll('script.jdgm-aggregate-rating-jld').forEach((s) => s.remove());
  };
  const watchJudgemeSchema = () => {
    dropJudgemeSchema();
    if (schemaWatch || !('MutationObserver' in window)) return;
    schemaWatch = new MutationObserver(dropJudgemeSchema);
    schemaWatch.observe(document.head, { childList: true });
    schemaWatch.observe(document.body, { childList: true });
  };

  // Put the hidden Judge.me widget in the page (once per page load) and have
  // Judge.me load it. Returns the host, or 'reload' when Judge.me was already
  // started for another product (it binds once per page load).
  let jdgmStartedFor = '';
  const startJudgeme = () => {
    const host = document.querySelector('[data-koc-jdgm-host]');
    if (!host) return null;
    const pid = host.dataset.productId || '';
    // A swap away and back can also drop the loaded widget (morph rebuilds the
    // host from the server markup): Judge.me cannot bind a second one, so reload.
    if (jdgmStartedFor) return jdgmStartedFor === pid && host.querySelector('.jdgm-widget') ? host : 'reload';
    jdgmStartedFor = pid;
    // Judge.me's manual-install markup (same as sections/product-tabs.liquid).
    const widget = document.createElement('div');
    widget.className = 'jdgm-widget jdgm-review-widget';
    widget.setAttribute('data-skip-node-update', '');
    widget.setAttribute('data-skip-subtree-update', '');
    Object.assign(widget.dataset, {
      productTitle: host.dataset.productTitle || '',
      id: pid,
      productId: pid,
      widget: 'review',
      autoInstall: 'false',
      shopReviewsCount: host.dataset.shopReviewsCount || '0',
      entryPoint: 'review_widget.js',
      entryKey: 'review-widget/main.js',
    });
    host.replaceChildren(widget);
    watchJudgemeSchema();
    // The loader (deferred, from the app embed) scans once and then sets this
    // flag; if it already ran, run a fresh copy so it finds the widget. If it
    // has not run yet, it finds the widget itself.
    const jdgm = window.jdgm;
    const loader = document.querySelector('script[src*="judgeme"][src*="loader.js"]');
    if (jdgm && jdgm._loaderExecuted && loader) {
      jdgm._loaderExecuted = false;
      const script = document.createElement('script');
      script.src = loader.src;
      script.async = true;
      document.body.appendChild(script);
    }
    return host;
  };

  // Write a review: open Judge.me's own form from the hidden widget.
  const writeReview = (button, pid) => {
    const host = startJudgeme();
    const hostPid = host instanceof Element ? host.dataset.productId : '';
    if (host === 'reload' || (hostPid && pid && hostPid !== pid)) {
      history.replaceState(null, '', location.pathname + location.search + WRITE_HASH);
      location.reload();
      return;
    }
    const find = () => {
      if (!host) return null;
      const direct = host.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link');
      if (direct) return direct;
      return [...host.querySelectorAll('button, a')].find((b) => /write a review/i.test(b.textContent || '')) || null;
    };
    const open = (target) => {
      button.removeAttribute('aria-busy');
      target.click();
    };
    const ready = find();
    if (ready) return open(ready);

    // Judge.me still loading (first tap, slow network): wait, then fall back to
    // showing its widget in place so the shopper can still write one.
    button.setAttribute('aria-busy', 'true');
    const started = Date.now();
    const timer = setInterval(() => {
      const target = find();
      if (target) {
        clearInterval(timer);
        open(target);
      } else if (Date.now() - started > 10000) {
        clearInterval(timer);
        button.removeAttribute('aria-busy');
        if (host) {
          host.classList.add('koc-reviews-jdgm-host--open');
          host.removeAttribute('aria-hidden');
          host.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 250);
  };

  // One instance per root element; new roots (after a morph) get one, kept
  // roots are re-synced. Idempotent: safe to run any number of times.
  const syncAll = () => {
    document.querySelectorAll('[data-koc-reviews]').forEach((root) => {
      const existing = instances.get(root);
      if (existing) existing.sync();
      else instances.set(root, new KocReviews(root));
    });
    // The 0-review empty state has no instance but moves the bar aside too
    // (observing the same element twice is a no-op).
    document.querySelectorAll('[data-koc-reviews-empty]').forEach((root) => viewObserver?.observe(root));
    syncInView(); // a morph may have removed a root that was on screen
  };

  const current = () => {
    const root = document.querySelector('[data-koc-reviews]');
    return root ? instances.get(root) : null;
  };

  const emptyRoot = () => document.querySelector('[data-koc-reviews-empty]');
  const revealAny = () => {
    const widget = current();
    if (widget) widget.reveal();
    else emptyRoot()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const init = () => {
    syncAll();

    // Review-request email links: let Judge.me run its own flow right away.
    if ((JDGM_PARAMS.test(location.search) || location.hash === '#judgeme') && (current() || emptyRoot())) {
      startJudgeme();
    }

    if (location.hash === WRITE_HASH) {
      history.replaceState(null, '', location.pathname + location.search);
      const write = () => {
        const widget = current();
        const root = widget?.root || emptyRoot();
        const button = root?.querySelector('[data-koc-write]');
        if (!root || !button) return;
        if (widget) widget.reveal();
        else root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        writeReview(button, root.dataset.productId);
      };
      if (document.readyState === 'complete') setTimeout(write, 300);
      else window.addEventListener('load', () => setTimeout(write, 300), { once: true });
    } else if (DEEP_LINK.test(location.hash)) {
      // Let the page settle (lazy media above us shifts layout), then scroll.
      setTimeout(revealAny, 600);
      window.addEventListener('load', () => setTimeout(revealAny, 300), { once: true });
    }
    window.addEventListener('hashchange', () => {
      if (DEEP_LINK.test(location.hash)) revealAny();
    });
    // The empty state has no widget instance; its one button is handled here.
    document.addEventListener('click', (e) => {
      const button = e.target instanceof Element ? e.target.closest('[data-koc-reviews-empty] [data-koc-write]') : null;
      if (!button) return;
      writeReview(button, button.closest('[data-koc-reviews-empty]').dataset.productId);
    });
    document.addEventListener('click', (e) => {
      const link = e.target instanceof Element ? e.target.closest('a[href^="#judgeme_product_reviews"], a[href^="#jdgm"]') : null;
      if (!link || link.closest('[data-koc-jdgm-host]')) return;
      e.preventDefault();
      history.replaceState(null, '', link.getAttribute('href'));
      revealAny();
    });

    // Horizon morphs <main> on a combined-listing product change, then fires
    // these (variant-picker.js). syncAll() is idempotent, so either may fire.
    document.addEventListener('variant-picker:updated', syncAll);
    document.addEventListener('variant:update', () => queueMicrotask(syncAll));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
